import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import React, { useMemo, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { PlayerPickerModal } from '@/components/fantasy/PlayerPickerModal';
import { PlayerAvatar } from '@/components/match/PlayerRatings';
import { TeamCrest } from '@/components/matches/TeamCrest';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Button } from '@/components/ui/Button';
import { ErrorState } from '@/components/ui/ErrorState';
import { Screen } from '@/components/ui/Screen';
import { Skeleton } from '@/components/ui/Skeleton';
import { useToast } from '@/components/ui/Toast';
import { useThemePalette } from '@/config/theme';
import { useFantasyPlayerPool, useFantasySeasons } from '@/features/fantasy/hooks';
import { useSubmitTotrPrediction, useTotrRound } from '@/features/games/teamOfRound';
import type { PlayerWithTeam, TotrPredictionPlayer, TotrRound } from '@/lib/data/provider';
import { localizeRound } from '@/lib/i18n/footballNames';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import {
  PITCH_VERTICAL_PADDING,
  PLAYER_TOKEN_WIDTH,
  ROW_GAP,
  ROW_MIN_HEIGHT,
} from '@/lib/pitch/formation';
import { errorKey } from '@/lib/utils/errors';
import type { Position } from '@scoring/rules.ts';

// A legal 4-4-2 from the attack down, mirroring the real Team of the Round.
const ROWS: { position: Position; count: number }[] = [
  { position: 'FWD', count: 2 },
  { position: 'MID', count: 4 },
  { position: 'DEF', count: 4 },
  { position: 'GK', count: 1 },
];
const SLOT_IDS = ROWS.flatMap((r) => Array.from({ length: r.count }, (_, i) => `${r.position}${i}`));

function shortName(p: PlayerWithTeam): string {
  return p.known_as ?? p.name.split(' ').slice(-1)[0];
}

/** Map a saved prediction back onto formation slots, using the loaded pool. */
function seedFromPrediction(prediction: TotrPredictionPlayer[], pool: PlayerWithTeam[]) {
  const byId = new Map(pool.map((p) => [p.id, p]));
  const picks: Record<string, PlayerWithTeam> = {};
  const counters: Record<string, number> = { GK: 0, DEF: 0, MID: 0, FWD: 0 };
  let captain: string | null = null;
  for (const pp of prediction) {
    const full = byId.get(pp.player_id);
    if (!full) continue;
    const slotId = `${pp.position}${counters[pp.position]++}`;
    picks[slotId] = full;
    if (pp.is_captain) captain = slotId;
  }
  return { picks, captain };
}

function Slot({
  player,
  isCaptain,
  onPick,
  onCaptain,
}: {
  player: PlayerWithTeam | null;
  isCaptain: boolean;
  onPick: () => void;
  onCaptain: () => void;
}) {
  const palette = useThemePalette();
  if (!player) {
    return (
      <Pressable
        accessibilityRole="button"
        onPress={onPick}
        className="items-center"
        style={{ width: PLAYER_TOKEN_WIDTH }}
      >
        <View
          className="h-[46px] w-[46px] items-center justify-center rounded-full border border-dashed"
          style={{ borderColor: palette.line }}
        >
          <Ionicons name="add" size={22} color={palette.inactive} />
        </View>
      </Pressable>
    );
  }
  return (
    <View className="items-center gap-1" style={{ width: PLAYER_TOKEN_WIDTH }}>
      <Pressable accessibilityRole="button" onPress={onPick}>
        <PlayerAvatar uri={player.photo_url} name={shortName(player)} size={46} />
        {/* Only the CAPTAIN shows the gold "C". Everyone else gets a faint star
            toggle (tap = make captain) - no more "C on every player". */}
        <Pressable
          accessibilityRole="button"
          onPress={onCaptain}
          hitSlop={8}
          className="absolute -right-1 -top-1 h-5 w-5 items-center justify-center rounded-full border"
          style={{
            backgroundColor: isCaptain ? palette.captain : palette.inset,
            borderColor: isCaptain ? palette.deep : palette.line,
          }}
        >
          {isCaptain ? (
            <AppText weight="bold" style={{ color: '#1A1408', fontSize: 10 }}>
              C
            </AppText>
          ) : (
            <Ionicons name="star-outline" size={11} color={palette.inactive} />
          )}
        </Pressable>
      </Pressable>
      <View className="flex-row items-center gap-1">
        {player.team?.logo_url ? <TeamCrest uri={player.team.logo_url} name={player.team.name} size="xs" /> : null}
        <AppText variant="micro" weight="medium" className="text-ink" numberOfLines={1} style={{ maxWidth: 56 }}>
          {shortName(player)}
        </AppText>
      </View>
    </View>
  );
}

/** Mounted only once round + pool are ready, so state seeds from props. */
function PredictForm({ round, pool }: { round: TotrRound; pool: PlayerWithTeam[] }) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const toast = useToast();
  const submit = useSubmitTotrPrediction();

  const seed = useMemo(() => seedFromPrediction(round.my_prediction, pool), [round.my_prediction, pool]);
  const [picks, setPicks] = useState<Record<string, PlayerWithTeam>>(seed.picks);
  const [captainSlot, setCaptainSlot] = useState<string | null>(seed.captain);
  const [activeSlot, setActiveSlot] = useState<{ id: string; position: Position } | null>(null);

  const pickedIds = useMemo(() => new Set(Object.values(picks).map((p) => p.id)), [picks]);
  const filledCount = Object.keys(picks).length;
  const captainId = captainSlot ? (picks[captainSlot]?.id ?? null) : null;
  const locked = round.locked;
  const ready = filledCount === 11 && captainId != null && !locked;

  const onPickPlayer = (player: PlayerWithTeam) => {
    if (!activeSlot) return;
    setPicks((prev) => ({ ...prev, [activeSlot.id]: player }));
    setActiveSlot(null);
  };

  const onSubmit = () => {
    if (!round.gameweek_id || !ready || !captainId) return;
    const playerIds = SLOT_IDS.map((id) => picks[id]?.id).filter(Boolean) as string[];
    submit.mutate(
      { gameweekId: round.gameweek_id, playerIds, captainId },
      {
        onSuccess: () => {
          toast.show({ message: t('totrPredict.saved'), tone: 'success' });
          router.back();
        },
        onError: (e) => toast.show({ message: t(errorKey(e)), tone: 'error' }),
      },
    );
  };

  return (
    <>
      <ScrollView contentContainerStyle={{ paddingBottom: 120, gap: 12 }}>
        <View className="mx-4 mt-1 flex-row items-center gap-3 rounded-card border border-line bg-surface px-4 py-3">
          <View className="h-10 w-10 items-center justify-center rounded-full bg-volt-glass">
            <Ionicons name="create" size={18} color={palette.voltBright} />
          </View>
          <View className="flex-1">
            <AppText variant="micro" className="uppercase tracking-wide text-muted">
              {round.round_label ? localizeRound(round.round_label, locale) : ''}
            </AppText>
            <AppText variant="caption" weight="medium" className="text-soft">
              {t('totrPredict.howto')}
            </AppText>
          </View>
        </View>

        {locked ? (
          <View className="mx-4 flex-row items-center gap-2 rounded-card border border-line bg-surface px-4 py-3">
            <Ionicons name="lock-closed" size={16} color={palette.inactive} />
            <AppText variant="caption" weight="medium" className="flex-1 text-soft">
              {t('totrPredict.locked')}
            </AppText>
          </View>
        ) : null}

        <View className="mx-4 overflow-hidden rounded-card border border-pitch-line bg-pitch px-2">
          {/* Anchored markings — consistent with the showcase + lineup pitches. */}
          <View className="absolute inset-x-3 top-1/2 h-px bg-pitch-line" />
          <View className="absolute left-1/2 top-1/2 h-16 w-16 -translate-x-8 -translate-y-8 rounded-full border border-pitch-line" />
          <View className="absolute left-1/2 top-1/2 h-1.5 w-1.5 -translate-x-[3px] -translate-y-[3px] rounded-full bg-pitch-line" />
          <View style={{ rowGap: ROW_GAP, paddingVertical: PITCH_VERTICAL_PADDING }}>
            {ROWS.map((row) => (
              <View
                key={row.position}
                className="flex-row items-center justify-evenly"
                style={{ minHeight: ROW_MIN_HEIGHT }}
              >
                {Array.from({ length: row.count }, (_, i) => {
                  const id = `${row.position}${i}`;
                  return (
                    <Slot
                      key={id}
                      player={picks[id] ?? null}
                      isCaptain={captainSlot === id}
                      onPick={() => {
                        if (!locked) setActiveSlot({ id, position: row.position });
                      }}
                      onCaptain={() => {
                        if (picks[id]) setCaptainSlot(id);
                      }}
                    />
                  );
                })}
              </View>
            ))}
          </View>
        </View>

        <AppText variant="micro" className="mx-4 text-center text-muted">
          {t('totrPredict.captainHint')}
        </AppText>
      </ScrollView>

      <View className="absolute bottom-0 left-0 right-0 border-t border-line bg-surface px-4 pb-6 pt-3">
        <AppText variant="micro" className="mb-2 text-center text-muted">
          {t('totrPredict.progress', { filled: filledCount })}
        </AppText>
        <Button
          label={locked ? t('totrPredict.locked') : t('totrPredict.save')}
          variant="primary"
          disabled={!ready}
          loading={submit.isPending}
          onPress={onSubmit}
        />
      </View>

      <PlayerPickerModal
        visible={activeSlot != null}
        position={activeSlot?.position ?? null}
        pool={pool}
        pickedIds={pickedIds}
        onPick={onPickPlayer}
        onClose={() => setActiveSlot(null)}
        hidePrice
      />
    </>
  );
}

export default function TotrPredictScreen() {
  const { t } = useTranslation();
  const round = useTotrRound();
  const seasons = useFantasySeasons();
  const wcSeason =
    (seasons.data ?? []).find((s) => (s.name ?? '').toLowerCase().includes('world cup')) ?? seasons.data?.[0];
  const pool = useFantasyPlayerPool(wcSeason?.competition_id ?? undefined);

  const loading = round.isPending || pool.isPending || seasons.isPending;

  return (
    <Screen>
      <BackHeader title={t('totrPredict.title')} />
      {loading ? (
        <View className="gap-3 px-4 pt-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-80 w-full" />
        </View>
      ) : round.isError ? (
        <ErrorState error={round.error} onRetry={() => round.refetch()} />
      ) : !round.data?.gameweek_id ? (
        <ErrorState error={new Error('NO_ROUND')} onRetry={() => round.refetch()} />
      ) : (
        <PredictForm round={round.data} pool={pool.data ?? []} />
      )}
    </Screen>
  );
}
